import type { Foe } from './foes';
import type { Transmission } from '@/game/coach/coach';
import type { AgeBand } from '@/game/coach/phrases';

/**
 * What Scout the Otter (the teammate at headquarters, voiced by Marcus) says in the transmission
 * stage, epic #44 (docs/design/transmission.md): he tells the child how to send the secret name,
 * asks for it, and confirms it. He never judges it: a clear r earns the bonus star; otherwise
 * Scout simply understood ("You must mean ..."); nothing heard is never called an r error. The
 * child says the name themselves (no read-along: children have heard r's all their lives; it is
 * making them that is hard, QA 2026-10-11); the speaker button reads it for a child who needs it.
 * Lines are recorded whole, names included, in Scout's voice (voice_sheets.py, "scout:" groups).
 */
export const TRANSMISSION = {
  /** The first time on a mission: how to send the name. */
  intro: {
    younger: [
      '[upbeat] Scout here, at headquarters! All the letters are up. Say the secret name out loud, and the mask comes off! Need help reading it? Tap the speaker.',
    ],
    older: [
      '[upbeat] Scout here, at headquarters. The codename is complete. Say it out loud, and we unmask them. Need help reading it? Tap the speaker.',
    ],
  },
  ask: ['Ready when you are. What is the secret name?', 'Go ahead, agent. Say the secret name.'],
  received: ['Got it. Name received!', 'Name received. Great teamwork!'],
  unclear: ["I couldn't hear the name. Say it again?"],
} as const;

/** The codename as spoken: "AGENT ROOK" becomes "Agent Rook" (so it is read, not spelled). */
export function spokenName(foe: Foe): string {
  return foe.codename.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

/** A clear r in the codename: the bonus star. */
export const clearR = (foe: Foe) => `Loud and clear: ${spokenName(foe)}! A strong r!`;

/** Not a clear r: Scout understood anyway. */
export const mustMean = (foe: Foe) => `You must mean ${spokenName(foe)}! Got it.`;

/** What the coach needs to run the transmission stage for this foe. */
export function transmissionFor(foe: Foe, ageBand: AgeBand = 'younger'): Transmission {
  return {
    text: spokenName(foe),
    phonemes: foe.spoken,
    lines: {
      intro: TRANSMISSION.intro[ageBand],
      ask: TRANSMISSION.ask,
      received: TRANSMISSION.received,
      unclear: TRANSMISSION.unclear,
      clear: clearR(foe),
      understood: mustMean(foe),
    },
  };
}
